import Color from 'color';
import {
  lFColor,
  lFColor0,
} from 'pages/components/LFHeader/LFHeader.styled';
import {
  borderColor,
  leftSidePanelMotionEase,
  logRes1,
  mobileRes2,
} from 'pages/styles';
import styled, { css, keyframes } from 'styled-components';
import { redColor } from './LogTable.styled';

export const scrollBarRes = '1150px';
/** App blue accent — never lavender/purple in this pane. */
const accentBlue = lFColor0;
const accentBlueMuted = Color(lFColor0).lighten(0.28).desaturate(0.15).toString();
const orange = Color('#d6a615').darken(0).toString();
const ink = '#1a1a1a';
const inkStrong = '#2a2a2a';
const inkBody = '#4a4a4a';
const inkMuted = '#6e6e6e';
const inkFaint = '#8a8a8a';

const panelBg = '#fafafa';
const line = Color('#e5e7eb').darken(0.02).toString();
/** Neutral grey border (no lavender tint). */
const lineSoft = Color('#e5e7eb').darken(0.08).toString();
const rowHover = Color(lFColor0).alpha(0.06).toString();
const rowSelected = Color(lFColor0).alpha(0.1).toString();

/* SKU-tab group headers: four greys only (no purple tint). */
const skuGroupLevelBg0 = 'white';
const skuGroupLevelBg1 = line;
const skuGroupLevelBg2 = panelBg;
const skuGroupLevelBg3 = lineSoft;
/** Scopes/SKU click-to-view: children under a white path parent (lighter grey). */
const scopesActiveChildBg = line;
const skuGroupLevelBackground = (level = 0) => {
  const shades = [
    skuGroupLevelBg0,
    skuGroupLevelBg1,
    skuGroupLevelBg2,
    skuGroupLevelBg3,
  ];
  return shades[((level % shades.length) + shades.length) % shades.length];
};

export const SearchCont = styled.div<{
  $clearEscape?: boolean;
  $belowPanelTitle?: boolean;
  /** Bleed past ActivityLogs padding so search stays edge-to-edge while scrolling. */
  $inScroll?: boolean;
}>`
  width: 100%;
  box-sizing: border-box;
  min-height: 50px;
  /* Slide-in panel: keep search below the absolute Escape (X) button. */
  padding: ${({ $clearEscape, $belowPanelTitle }) => {
    if ($clearEscape) return '52px 12px 12px 12px';
    if ($belowPanelTitle) return '10px 12px 12px 12px';
    return '14px 12px 12px 12px';
  }};
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: white;
  border-bottom: 1px solid ${line};
  ${({ $inScroll }) => ($inScroll ? `
    margin: -8px -10px 0;
    width: calc(100% + 20px);
    flex-shrink: 0;
    @media(max-width: ${mobileRes2}) {
      margin-left: -12px;
      margin-right: -12px;
      width: calc(100% + 24px);
    }
  ` : '')}
`;

export const SearchRow = styled.div<{ $barLayout?: boolean, $inline?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  ${({ $barLayout, $inline }) => (($barLayout || $inline) ? `
    flex: 1 1 200px;
    min-width: 160px;
    ${$barLayout ? 'max-width: 320px;' : ''}
    width: auto;
  ` : '')}
`;

export const SearchInputWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

export const IconDiv = styled.div`
  position: absolute;
  top: 50%;
  left: 11px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  color: ${inkMuted};
  pointer-events: none;
  z-index: 1;

  & > svg {
    font-size: 20px;
  }
`;

export const Input = styled.input<{ $hasClear?: boolean; $hideLeftIcon?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  height: 34px;
  padding-left: ${({ $hideLeftIcon }) => ($hideLeftIcon ? '10px' : '40px')};
  padding-right: ${({ $hasClear }) => ($hasClear ? '36px' : '9px')};
  border-radius: 8px;
  border: 1px solid ${lineSoft};
  outline: 0;
  font-size: 12px;
  color: ${inkStrong};
  font-family: Poppins, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  font-weight: 300;
  background: white;

  &::placeholder {
    color: ${inkFaint};
  }
`;

export const ClearSearchBtn = styled.button`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  padding: 0;
  cursor: pointer;
  background: rgba(0, 0, 0, 0.08);
  color: ${inkMuted};
  z-index: 1;

  &:hover {
    background: rgba(0, 0, 0, 0.14);
  }

  & > svg {
    font-size: 16px;
  }
`;

export const AddSearchBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1.5px solid ${lineSoft};
  background: white;
  color: ${borderColor};
  cursor: pointer;
  padding: 0;
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    border-color: ${borderColor};
    color: ${Color(borderColor).darken(0.2).toString()};
  }

  & > svg {
    font-size: 22px;
  }
`;

const negateActive = '#b42318';

const optionsAccent = '#3d5a80';
const optionsAccentHot = '#2c4a6e';
const chipAddedNegateBg = Color(negateActive).lighten(0.05).toString();
const chipAccentIdleBg = Color(lFColor0).lighten(0.86).toString();
const chipAccentIdleBorder = Color(lFColor0).lighten(0.72).toString();
const chipAccentActiveBg = Color(lFColor0).lighten(0.8).toString();
const chipNegateIdleBg = Color(negateActive).lighten(0.78).toString();
const chipNegateIdleBorder = Color(negateActive).lighten(0.55).toString();
const chipNegateIdleFg = Color(negateActive).darken(0.22).toString();

export const SearchOptionsBtn = styled.button<{ $open?: boolean; $custom?: boolean; $compact?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({ $compact }) => ($compact ? '22px' : '34px')};
  height: ${({ $compact }) => ($compact ? '22px' : '34px')};
  border-radius: ${({ $compact }) => ($compact ? '50%' : '8px')};
  border: ${({ $compact, $custom, $open }) => {
    if ($compact) return 'none';
    if ($custom) return `1.5px solid ${optionsAccent}`;
    if ($open) return `1.5px solid ${borderColor}`;
    return `1.5px solid ${lineSoft}`;
  }};
  background: ${({ $custom, $open, $compact }) => {
    if ($custom) return optionsAccent;
    if ($compact) return 'rgba(255, 255, 255, 0.14)';
    if ($open) return 'rgba(0, 0, 0, 0.04)';
    return 'white';
  }};
  color: ${({ $custom, $compact }) => {
    if ($custom) return 'white';
    if ($compact) return 'white';
    return borderColor;
  }};
  cursor: pointer;
  padding: 0;
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease;

  &:hover {
    background: ${({ $custom, $compact }) => {
    if ($custom) return optionsAccentHot;
    if ($compact) return 'rgba(255, 255, 255, 0.28)';
    return 'rgba(0, 0, 0, 0.04)';
  }};
    border-color: ${({ $custom, $compact }) => {
    if ($compact) return 'transparent';
    if ($custom) return optionsAccentHot;
    return borderColor;
  }};
    color: ${({ $custom, $compact }) => {
    if ($custom || $compact) return 'white';
    return Color(borderColor).darken(0.2).toString();
  }};
  }

  & > svg {
    font-size: ${({ $compact }) => ($compact ? '14px' : '20px')};
  }
`;

export const SearchOptionsAnchor = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const SearchOptionsPopup = styled.div`
  position: fixed;
  z-index: 1300;
  width: min(292px, calc(100vw - 24px));
  padding: 10px 10px 12px;
  background: white;
  border: 1px solid ${Color(lineSoft).lighten(0.05).toString()};
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14), 0 0 0 1px rgba(0, 0, 0, 0.04);
`;

export const SearchOptionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const SearchOptionsSectionLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${inkFaint};
  margin: 4px 4px 6px;
`;

export const SearchOptionsDivider = styled.div`
  height: 1px;
  background: ${Color('lightgrey').lighten(0.08).toString()};
  margin: 8px 2px;
`;

export const SearchOptionsClearBtn = styled.button<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 4px;
  padding: 8px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ $disabled }) => ($disabled ? inkFaint : negateActive)};
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
  transition: background 100ms ease;

  &:hover {
    background: ${({ $disabled }) => (
    $disabled ? 'transparent' : Color(negateActive).alpha(0.08).toString()
  )};
  }

  & > svg {
    font-size: 16px;
  }
`;

export const SearchOptionRow = styled.button<{ $checked?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: none;
  border-radius: 6px;
  padding: 8px 8px;
  background: ${({ $checked }) => (
    $checked ? Color(optionsAccent).alpha(0.1).toString() : 'transparent'
  )};
  color: ${inkStrong};
  cursor: pointer;
  transition: background 100ms ease;

  &:hover {
    background: ${({ $checked }) => (
    $checked
      ? Color(optionsAccent).alpha(0.14).toString()
      : 'rgba(0, 0, 0, 0.035)'
  )};
  }
`;

export const SearchOptionCheck = styled.span<{ $checked?: boolean; $radio?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-top: 1px;
  border-radius: ${({ $radio }) => ($radio ? '50%' : '3px')};
  border: 1.5px solid ${({ $checked }) => (
    $checked ? optionsAccent : Color(lineSoft).darken(0.05).toString()
  )};
  background: ${({ $checked }) => ($checked ? optionsAccent : 'white')};
  color: white;

  & > svg {
    font-size: 12px;
  }
`;

export const SearchOptionIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  margin-top: 0;
  color: ${inkMuted};

  & > svg {
    font-size: 18px;
  }
`;

export const SearchOptionText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
`;

export const SearchOptionTitle = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.25;
  color: ${inkStrong};
`;

export const SearchOptionHint = styled.span`
  font-size: 10.5px;
  font-weight: 400;
  line-height: 1.3;
  color: ${inkMuted};
`;

export const SearchChips = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

export const SearchChip = styled.div<{ $negate?: boolean; $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  min-height: 34px;
  padding: 6px 8px 6px 10px;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: default;
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease;

  ${({ $negate, $active }) => {
    if ($negate && $active) {
      return `
        background: ${chipAddedNegateBg};
        color: white;
        border-color: transparent;
      `;
    }
    if ($negate) {
      return `
        background: ${chipNegateIdleBg};
        color: ${chipNegateIdleFg};
        border-color: ${chipNegateIdleBorder};

        & > span {
          opacity: 1;
          color: ${chipNegateIdleFg};
        }

        & > button {
          background: rgba(180, 35, 24, 0.16);
          color: ${chipNegateIdleFg};
        }

        & > button:hover {
          background: rgba(180, 35, 24, 0.26);
          color: ${chipNegateIdleFg};
        }
      `;
    }
    if ($active) {
      return `
        background: ${chipAccentActiveBg};
        color: ${lFColor};
        border-color: ${chipAccentIdleBorder};
      `;
    }
    return `
      background: ${chipAccentIdleBg};
      color: ${lFColor};
      border-color: ${chipAccentIdleBorder};
    `;
  }}

  ${SearchOptionsBtn} {
    ${({ $negate, $active }) => {
    if ($negate && $active) {
      return `
        background: rgba(255, 255, 255, 0.14);
        color: white;
        &:hover { background: rgba(255, 255, 255, 0.28); color: white; }
      `;
    }
    if ($negate) {
      return `
        background: ${negateActive};
        color: #fff !important;
        &:hover { background: ${Color(negateActive).darken(0.12).toString()}; color: #fff !important; }
      `;
    }
    return `
      background: ${Color(lFColor0).alpha(0.1).toString()};
      color: ${lFColor};
      &:hover { background: ${Color(lFColor0).alpha(0.18).toString()}; color: ${lFColor}; }
    `;
  }}
  }
`;

export const SearchChipIcon = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  opacity: 0.9;

  & > svg {
    font-size: 16px;
  }
`;

export const SearchChipText = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SearchChipInput = styled.input`
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: none;
  outline: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.3;
  caret-color: currentColor;

  &::placeholder {
    color: currentColor;
    opacity: 0.45;
  }
`;

export const SearchChipRemove = styled.button<{ $solid?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background: ${({ $solid }) => (
    $solid ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.08)'
  )};
  color: ${({ $solid }) => ($solid ? 'white' : 'inherit')};
  cursor: pointer;
  padding: 0;

  &:hover {
    background: ${({ $solid }) => (
    $solid ? 'rgba(255, 255, 255, 0.28)' : 'rgba(0, 0, 0, 0.14)'
  )};
  }

  & > svg {
    font-size: 15px;
  }
`;

export const Header = styled.div`
  width: 100%;
  box-sizing: border-box;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 4px 14px 12px;
  color: ${ink};
  text-align: left;
`;

/** Mobile slide-in title row — replaces absolute Escape + duplicate header. */
export const MobilePanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;
  padding: 14px 12px 12px 14px;
  background: #fff;
  border-bottom: 1px solid ${line};
`;

export const MobilePanelTitle = styled.div`
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${ink};
  line-height: 1;
`;

export const MobilePanelClose = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  margin: 0;
  padding: 0;
  border: 1px solid ${line};
  border-radius: 8px;
  background: #fff;
  color: ${inkMuted};
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover {
    background: ${rowHover};
    border-color: ${Color(lFColor0).alpha(0.28).toString()};
    color: ${lFColor0};
  }
`;

/* List scrolls as one unit — no sticky/fixed chrome inside the pane. */
export const ActivityLogs = styled.div`
  flex: 1;
  padding: 8px 10px 24px;
  box-sizing: border-box;
  color: ${inkBody};
  overflow-y: auto;
  position: relative;
  @media(max-width: ${mobileRes2}) {
    padding: 8px 12px 24px;
  }
`;

export const ActivityCont = styled.div<{ isCollapsed?: boolean; $compactBottom?: boolean }>`
  width: 100%;
  padding-bottom: ${({ $compactBottom, isCollapsed }) => {
    if ($compactBottom) return '2px';
    return isCollapsed ? '8px' : '16px';
  }};

  & + & {
    margin-top: 2px;
  }
`;

export const ActivityGroup1 = styled.div<{ $selected?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  font-weight: ${({ $selected }) => ($selected ? 600 : 500)};
  font-size: 12.5px;
  color: ${({ $selected }) => ($selected ? lFColor0 : inkStrong)};
  padding: 9px 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  border-radius: 8px;
  background: ${({ $selected }) => ($selected ? rowSelected : 'transparent')};
  transition: background 0.12s, color 0.12s;

  &:hover {
    background: ${({ $selected }) => ($selected ? rowSelected : rowHover)};
    color: ${lFColor0};
  }
  @media(max-width: ${mobileRes2}) {
    font-size: 12.5px;
  }
`;

export const ActivityGroup0 = styled(ActivityGroup1)`
  padding: 9px 15px;
  color: white;
  font-weight: 700;
  background: ${Color(lFColor).lighten(0.2).toString()};
  border-radius: 8px;
  font-size: 12px;
  margin-top: 5px;
  margin-bottom: 12px;
  &:hover {
    color: white;
    background: ${Color(orange).lighten(0.2).toString()};
  }
`;

export const ActivityGroup = styled(ActivityGroup1)<{
  $level?: number;
  $objectGroup?: boolean;
  /** Scopes click-to-view: path parents white, current-level scopes grey. */
  $scopesSurface?: 'white' | 'active';
}>`
  align-items: center;
  /* Path parents + root: white. Current drill level: light grey. */
  ${({ $objectGroup, $level, $scopesSurface }) => {
    if ($scopesSurface === 'white') {
      return `
        background: white;
        color: ${inkBody};
        &:hover {
          background: ${rowHover};
          color: ${lFColor0};
        }
      `;
    }
    if ($scopesSurface === 'active') {
      return `
        background: ${scopesActiveChildBg};
        color: ${inkBody};
        padding: 7px 8px;
        &:hover {
          background: ${rowHover};
          color: ${lFColor0};
        }
      `;
    }
    if ($objectGroup) {
      return `
        background: transparent;
        color: ${inkStrong};
        &:hover {
          background: ${rowHover};
          color: ${inkStrong};
        }
      `;
    }
    if ($level === undefined) return '';
    return `
      background: ${skuGroupLevelBackground($level)};
      color: ${inkBody};
      &:hover {
        background: ${rowHover};
        color: ${lFColor0};
      }
    `;
  }}
`;

export const ActivityGroupLabel = styled.span`
  flex: 1;
  min-width: 0;
  line-height: 1.3;
`;

/** Drill hint on expandable scopes — down when collapsed, exit-door when children show. */
export const ScopeDrillIcon = styled.span<{ $mode?: 'down' | 'up' | 'exit' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  color: ${Color(lFColor0).alpha(0.72).toString()};
  transition: color 0.14s ${leftSidePanelMotionEase}, transform 0.18s ${leftSidePanelMotionEase};

  svg {
    display: block;
    font-size: 18px;
  }

  ${ActivityGroup}:hover & {
    color: ${lFColor0};
  }
`;

export const ActivityGroupSticky = styled.div<{ $depth?: number; $level?: number; $objectGroup?: boolean }>`
  /* Header + search scroll with the list (not sticky/fixed). */
  background: ${panelBg};
  margin-left: -10px;
  margin-right: -10px;
  padding: 4px 10px;

  ${ActivityGroup} {
    background: ${({ $objectGroup, $level }) => {
    if ($objectGroup) return 'transparent';
    if ($level === undefined) return 'white';
    return skuGroupLevelBackground($level);
  }};
  }

  @media(max-width: ${mobileRes2}) {
    margin-left: -12px;
    margin-right: -12px;
    padding-left: 12px;
    padding-right: 12px;
  }
`;

const groupChildrenIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/** Child groups stay flush with the parent; only SKU rows use $skuIndent. */
export const ActivityGroupChildren = styled.div<{
  $nested?: boolean;
  $skuIndent?: boolean;
  /** Deepest open scope’s children — tighter pad so they sit denser than path parents. */
  $symmetricActivePad?: boolean;
}>`
  padding-left: ${({ $skuIndent, $nested, $symmetricActivePad }) => {
    if ($symmetricActivePad) return '23px';
    if ($skuIndent || $nested) return '10px';
    return '0';
  }};
  ${({ $symmetricActivePad }) => ($symmetricActivePad ? `
    padding-top: 4px;
    padding-bottom: 4px;
  ` : '')}
  animation: ${groupChildrenIn} 220ms ${leftSidePanelMotionEase};
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const EmptyGroupsHint = styled.div`
  padding: 14px 10px;
  font-size: 12px;
  color: ${inkMuted};
  line-height: 1.4;
`;

/** Recap of selected scopes under the tree — muted, one name per line. */
export const ActiveScopesPreview = styled.div`
  margin-top: 8px;
  padding: 10px 8px 6px;
  border-top: 1px solid ${line};
  display: flex;
  flex-direction: column;
  gap: 1px;
  animation: ${groupChildrenIn} 220ms ${leftSidePanelMotionEase};
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const ActiveScopeName = styled.div`
  font-size: 11.5px;
  line-height: 1.5;
  color: ${inkMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ActiveScopesMore = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${inkFaint};
  padding-top: 2px;
`;

export const GroupTypeSearch = styled.div<{ $skuTab?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  margin-top: ${({ $skuTab }) => ($skuTab ? '3px' : '0')};
  margin-bottom: ${({ $skuTab }) => ($skuTab ? '10px' : '5px')};
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 10px;
  border: 1px solid ${line};
  border-radius: 8px;
  background: white;
  color: ${inkMuted};
  transition: border-color 120ms ease, background 120ms ease, color 120ms ease;

  &:focus-within {
    border-color: ${Color(lFColor0).alpha(0.35).toString()};
    background: white;
    color: ${inkBody};
  }

  & > svg {
    font-size: 14px;
    flex-shrink: 0;
  }
`;

export const GroupTypeSearchInput = styled.input`
  width: 100%;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 11.5px;
  font-weight: 400;
  color: ${inkStrong};
  font-family: Poppins, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  padding: 0;
  line-height: 1.3;

  &::placeholder {
    color: ${inkFaint};
    font-weight: 400;
  }
`;

export const Select = styled.input`
  height: 16px;
  width: 16px;
  margin: 0;
  flex-shrink: 0;
  accent-color: ${accentBlue};
`;

export const CheckBox = styled.span<{
  $state: 'off' | 'partial' | 'on',
  /** VO/BO: grey check, not accent blue. */
  $neutral?: boolean,
  /** Scopes tab: checkbox alone toggles selection. */
  $clickable?: boolean,
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: 16px;
  width: 16px;
  margin: 0;
  flex-shrink: 0;
  border-radius: 4px;
  border: 1.5px solid
    ${({ $state, $neutral }) => {
    if ($state === 'on') return $neutral ? inkMuted : accentBlue;
    if ($state === 'partial') return $neutral ? inkFaint : accentBlueMuted;
    return Color(lineSoft).darken(0.05).toString();
  }};
  background: ${({ $state, $neutral }) => {
    if ($state === 'on') return $neutral ? inkMuted : accentBlue;
    if ($state === 'partial') return $neutral ? inkFaint : accentBlueMuted;
    return 'white';
  }};
  color: white;
  pointer-events: ${({ $clickable }) => ($clickable ? 'auto' : 'none')};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  & > svg {
    font-size: 12px;
  }
`;

export const DropDownMarker = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;
  padding: 2px 4px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${inkMuted};
  cursor: pointer;
  font: inherit;
  line-height: 1;
  flex-shrink: 0;

  &:hover {
    color: ${lFColor0};
    background: ${rowHover};
  }

  & > svg {
    font-size: 18px;
  }
`;

export const DropDownCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  min-width: 1.5em;
  text-align: right;
  color: inherit;
  white-space: nowrap;
`;

export const Select2 = styled(Select)``;

export const ActivityName = styled.div<{
  isDeleted?: boolean;
  isSelected?: boolean;
  /** Scopes click-to-view leaf rows: match active/white surfaces. */
  $scopesSurface?: 'white' | 'active';
}>`
  width: 100%;
  box-sizing: border-box;
  font-size: 12.5px;
  font-weight: ${({ isSelected }) => (isSelected ? 600 : 400)};
  color: ${({ isDeleted, isSelected }) => {
    if (isDeleted) return inkFaint;
    if (isSelected) return lFColor0;
    return inkBody;
  }};
  padding: ${({ $scopesSurface }) => ($scopesSurface === 'active' ? '7px 8px' : '8px 8px')};
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  border-radius: 8px;
  background: ${({ isSelected, $scopesSurface }) => {
    if ($scopesSurface === 'white') return 'white';
    if ($scopesSurface === 'active') return scopesActiveChildBg;
    if (isSelected) return rowSelected;
    return 'transparent';
  }};
  transition: background 0.12s, color 0.12s;
  ${ActivityGroupSticky} + & {
    margin-top: 2px;
  }
  &:hover {
    background: ${({ isSelected, $scopesSurface }) => {
    if ($scopesSurface) return rowHover;
    if (isSelected) return rowSelected;
    return rowHover;
  }};
    color: ${({ isDeleted }) => (isDeleted ? inkMuted : lFColor0)};
  }
`;

export const ActivityNameText = styled.span<{ isDeleted?: boolean }>`
  flex: 1;
  min-width: 0;
  line-height: 1.3;
  text-decoration: ${({ isDeleted }) => (isDeleted ? 'line-through' : 'none')};
  text-decoration-thickness: 1px;
`;

export const DeletedIcon = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: ${inkFaint};
  line-height: 1;
`;

export const EsacpePart = styled.div`
  display: none;
  width: 100%;
  height: 0px;
  overflow: visible;
  position: absolute;
  top: 0;
  right: 0;
  @media(max-width: ${logRes1}) {
    display: block;
  }
`;

/** Combine SKUs — rolls up value logs by type in the ActivityLogs table. */
export const CombineSkuToggle = styled.button<{ $inline?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ $inline }) => ($inline ? '6px' : '8px')};
  box-sizing: border-box;
  ${({ $inline }) => ($inline ? `
    /* Sit at the right of Time / + / − / Cash. */
    width: auto;
    margin: 0 0 0 auto;
    padding: 7px 9px;
  ` : `
    /* ActivityLogs has 10px inset; +4px each side → 14px from pane edge (same as before). */
    width: calc(100% - 8px);
    margin: 0 4px 8px;
    padding: 8px 10px;
  `)}
  border: 1px solid ${line};
  border-radius: 8px;
  background: #fff;
  color: ${inkBody};
  font-size: 12px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  flex-shrink: 0;
  &:hover {
    background: ${rowHover};
    border-color: ${Color(lFColor0).alpha(0.35).toString()};
  }
`;

export const CombineSkuCheck = styled.input`
  width: 14px;
  height: 14px;
  margin: 0;
  accent-color: ${lFColor0};
  pointer-events: none;
`;

const tableFilterIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/** Wrapper receives hover — disabled chips use pointer-events: none so tooltips still show. */
export const TableFilterChipWrap = styled.span<{ $disabled?: boolean }>`
  display: inline-flex;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'default')};
`;

/** Time / + / − / Cash — scrolls with the ActivityLogs list (14px from pane edge). */
export const TableFilterRow = styled.div<{ $inline?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  box-sizing: border-box;
  ${({ $inline }) => ($inline ? css`
    width: auto;
    margin: 0;
    padding: 0;
    animation: none;
  ` : css`
    /* Cancel ActivityLogs 10px inset, then apply the same 14px side inset as before. */
    width: calc(100% + 20px);
    margin: 8px -10px;
    padding: 0 14px;
    animation: ${tableFilterIn} 280ms ${leftSidePanelMotionEase};
    @media(max-width: ${mobileRes2}) {
      width: calc(100% + 24px);
      margin-left: -12px;
      margin-right: -12px;
    }
    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `)}
`;

export const TableFilterChip = styled.button<{ $on?: boolean, $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin: 0;
  padding: 7px 9px;
  border: 1px solid ${({ $on, $danger }) => ($on
    ? Color($danger ? redColor : lFColor0).alpha(0.35).toString()
    : line)};
  border-radius: 8px;
  background: ${({ $on, $danger }) => ($on
    ? Color($danger ? redColor : lFColor0).alpha(0.1).toString()
    : '#fff')};
  color: ${({ $on, $danger }) => ($on
    ? ($danger ? redColor : lFColor0)
    : inkBody)};
  font-size: 12px;
  font-weight: 400;
  font-family: inherit;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  transition: background 160ms ease, color 160ms ease, border-color 160ms ease;
  &:hover:not(:disabled) {
    background: ${({ $on, $danger }) => ($on
      ? Color($danger ? redColor : lFColor0).alpha(0.14).toString()
      : rowHover)};
    border-color: ${({ $on, $danger }) => ($on
      ? Color($danger ? redColor : lFColor0).alpha(0.45).toString()
      : Color(lFColor0).alpha(0.35).toString())};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  }
  svg {
    flex-shrink: 0;
  }
`;

export const TableFilterSign = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 11px;
  font-size: 15px;
  font-weight: 600;
  line-height: 1;
`;
